import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Languages, Loader2 } from "lucide-react";
import { DISCIPLINES } from "../data/disciplines";

const BG_IMAGE = "https://images.unsplash.com/photo-1764314138160-5f04f4a50dae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcGlzdCUyMGNsaW5pY3xlbnwwfHx8fDE3NzgxMDM2NzJ8MA&ixlib=rb-4.1.0&q=85";


export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || null;
  const [form, setForm] = useState({ full_name: "", email: "", password: "", discipline: "OT" });
  const [busy, setBusy] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${inviteToken}`);
        setInviteInfo(data);
        if (data.email) setForm((f) => ({ ...f, email: data.email }));
        if (data.discipline) setForm((f) => ({ ...f, discipline: data.discipline }));
      } catch {
        toast.error("Invite link is invalid or expired");
      }
    })();
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const user = await register({ ...form, invite_token: inviteToken || undefined });
      toast.success("Account created");
      // Org members go straight to /app (their org's sub covers them);
      // solo users hit the paywall and choose a plan.
      nav(user.has_active_subscription ? "/app" : "/billing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(${BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-zinc-200 bg-white/40 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-xl tracking-tight text-zinc-900">Clinico Translate</span>
        </div>

        <div className="max-w-lg">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-6">Built for therapists</p>
          <h1 className="font-heading text-5xl xl:text-6xl tracking-tight leading-none text-zinc-900 mb-6">
            Start treating every patient — in their language.
          </h1>
          <p className="text-lg text-zinc-600 leading-relaxed">
            Free for individual clinicians. Pick your discipline and get a phrase library tailored for OT, ST, PT, COTA, and PTA sessions.
          </p>
        </div>

        <p className="text-sm text-zinc-500">© 2026 Clinico Translate</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8">
          <h2 className="font-heading text-3xl tracking-tight text-zinc-900 mb-2">Create your account</h2>
          <p className="text-zinc-600 mb-8">It takes less than a minute.</p>

          {inviteInfo && (
            <div className="mb-6 border border-zinc-900 rounded-lg bg-zinc-900 text-white p-4" data-testid="invite-banner">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">You've been invited</p>
              <p className="text-sm">
                Join <span className="font-medium">{inviteInfo.org_name}</span> on Clinico Translate
                {inviteInfo.discipline && <> as <span className="font-medium">{inviteInfo.discipline}</span></>}.
                Billing is covered by your clinic admin.
              </p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="full_name" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Full name</Label>
              <Input
                id="full_name"
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-2 h-12 text-base"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 h-12 text-base"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm uppercase tracking-[0.2em] text-zinc-500">Password</Label>
              <PasswordInput
                id="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 h-12 text-base"
                data-testid="register-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-sm uppercase tracking-[0.2em] text-zinc-500">Discipline</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                {DISCIPLINES.map((d) => (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => setForm({ ...form, discipline: d.code })}
                    className={`h-12 rounded-lg border text-sm font-medium transition-colors duration-200 ${
                      form.discipline === d.code
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                    }`}
                    data-testid={`register-discipline-${d.code}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {DISCIPLINES.find((d) => d.code === form.discipline)?.full}
              </p>
            </div>

            <Button type="submit" disabled={busy} className="w-full h-12 text-base" data-testid="register-submit-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>

            <p className="text-xs text-zinc-500 text-center leading-relaxed" data-testid="register-legal-notice">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="underline underline-offset-2 hover:text-zinc-800">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-zinc-800">Privacy Policy</Link>.
            </p>
          </form>

          <p className="text-sm text-zinc-600 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-zinc-900 underline underline-offset-4 font-medium" data-testid="register-go-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
